import create from "zustand";

import { errorAwareApiFetch } from "../../../../../apiBindings";
import { storageLocal as storage } from "../../../../../utils/StorageHandler";
import createFormErrorSlices from "../../../../../utils/store/tsFormErrors";
import Validator from "../../../../../utils/validation/Validator";
import resolveWebView from "../../../../../utils/webViewRedirect";
import { navigate } from "../../../../shared/LinkComponent";
import iAdditionalAddress from "../../interfaces/iAdditionalAddress";
import iCheckoutData from "../../interfaces/iCheckoutData";
import iDeliveryAddress from "../../interfaces/iDeliveryAddress";
import iNotify from "../../interfaces/iNotify";

interface iStepDeliveryAddress {
    up: number;
    useInvoiceAddress: boolean;
    additionalAddresses: Array<iAdditionalAddress>;
    amountFirstUnloadingPoint: number;
    deliveryAddress: iDeliveryAddress;
    invoiceAddress: iDeliveryAddress;
    setUseInvoiceAddress: (useInvoiceAddress: boolean) => void;
    setDeliveryAddress: (deliveryAddress: iDeliveryAddress) => void;
    setInvoiceAddress: (invoiceAddress: iDeliveryAddress) => void;
    setAdditionalAddresses: (
        additionalAddresses: Array<iAdditionalAddress>
    ) => void;
    setAmountFirstUnloadingPoint: (amount: number) => void;
    increaseUnloadingPoints: () => void;
    storeUnloadingPointsToServer: (
        checkoutData: iCheckoutData,
        notify: iNotify
    ) => Promise<iCheckoutData>;
    changeSelectedStreet: (street: string) => void;
    changeSelectedCity: (city: string) => void;
    getAmountForFirstUnloadingPoint: (amount: number) => number;
    removeAdditionalAddress: (index: number) => void;
    addAdditionalAddress: (address: iAdditionalAddress) => void;
    updateAdditionalAddress: (
        address: iAdditionalAddress,
        index: number
    ) => void;
    storeDeliveryAddressToServer: (
        checkoutData: iCheckoutData,
        notify: iNotify,
        isOverview: boolean,
        onSuccess: () => void | undefined
    ) => Promise<iCheckoutData>;
    errors: string[];
    setErrors: (errors: string[]) => void;
    addError: (error: string) => void;
    clearError: (error: string) => void;
    hasErrorAt: (error: string) => boolean;
    geoDataError: boolean;
    onGooglePlacesChange: (placesData: any) => void;
}

const useStore = create<iStepDeliveryAddress>((set, get) => ({
    ...createFormErrorSlices(set, get),
    up: storage.getInt("hel:up", 1),
    useInvoiceAddress: storage.getBoolean("hel:use-invoice-address"),
    additionalAddresses: storage.getJson("hel:additional-addresses", []),
    amountFirstUnloadingPoint: 0,
    deliveryAddress: storage.getJson("hel:delivery-address", {
        company: "",
        salutation: "Herr",
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        street: "",
        house_number: "",
        zipcode: storage.get("hel:zipcode"),
        city: "",
    }),
    invoiceAddress: storage.getJson("hel:invoice-address", {
        company: "",
        salutation: "Herr",
        firstname: "",
        lastname: "",
        street: "",
        house_number: "",
        zipcode: "",
        city: "",
    }),
    geoDataError: false,

    setUseInvoiceAddress: (useInvoiceAddress: boolean) => {
        set({ useInvoiceAddress: useInvoiceAddress });
    },

    setDeliveryAddress: (deliveryAddress) => {
        set({ deliveryAddress: deliveryAddress });
    },

    setInvoiceAddress: (invoiceAddress) => {
        set({ invoiceAddress: invoiceAddress });
    },

    increaseUnloadingPoints: () => {
        const newUp = get().up + 1;
        storage.set("hel:up", newUp);
        set({ up: newUp });
    },

    decreaseUnloadingPoints: () => {
        const newUp = get().up - 1;
        storage.set("hel:up", newUp);
        set({ up: newUp });
    },

    storeUnloadingPointsToServer: async (
        checkoutData,
        notify
    ): Promise<iCheckoutData> => {
        let checkoutDataResponse = { ...checkoutData };
        const data = {
            hash: checkoutData.user_hash,
            // We use it from storage here, because the state may be not on the new value yet.
            unloadingPoints: storage.get("hel:up"),
            calculateOnly: false,
        };

        await errorAwareApiFetch<{ data: any }>(
            `/heatingoil/checkout/unloadingPoints`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        )
            .then(({ data }) => {
                checkoutDataResponse = data;
            })
            .catch(({ data }) => {
                notify("Fehlgeschlagen", data.message);
            });
        return checkoutDataResponse;
    },

    changeSelectedStreet: (street) => {
        get().clearError("deliveryAddress.street");
        set({
            deliveryAddress: {
                ...get().deliveryAddress,
                street: street,
            },
        });
        storage.setJson("hel:selected-street", street);
    },

    changeSelectedCity: (city: string) => {
        get().clearError("deliveryAddress.city");
        set({
            deliveryAddress: {
                ...get().deliveryAddress,
                city: city,
            },
        });
        storage.setJson("hel:selected-city", city);
    },

    onGooglePlacesChange: (placesData, isInvoiceAddress = false) => {
        const result = placesData[0];

        result.address_components.forEach((address: any) => {
            if (address.types.includes("route")) {
                if (isInvoiceAddress) {
                    set({
                        invoiceAddress: {
                            ...get().invoiceAddress,
                            street: address.long_name,
                        },
                    });
                } else {
                    set({
                        deliveryAddress: {
                            ...get().deliveryAddress,
                            street: address.long_name,
                        },
                    });
                }
            }
            if (address.types.includes("street_number")) {
                if (isInvoiceAddress) {
                    set({
                        invoiceAddress: {
                            ...get().invoiceAddress,
                            house_number: address.long_name,
                        },
                    });
                } else {
                    set({
                        deliveryAddress: {
                            ...get().deliveryAddress,
                            house_number: address.long_name,
                        },
                    });
                }
            }
            if (address.types.includes("locality")) {
                if (isInvoiceAddress) {
                    set({
                        invoiceAddress: {
                            ...get().invoiceAddress,
                            city: address.long_name,
                        },
                    });
                } else {
                    set({
                        deliveryAddress: {
                            ...get().deliveryAddress,
                            city: address.long_name,
                        },
                    });
                }
            }
            if (address.types.includes("postal_code") && isInvoiceAddress) {
                set({
                    invoiceAddress: {
                        ...get().invoiceAddress,
                        zipcode: address.long_name,
                    },
                });
            }
        });
    },

    getState: () => {
        return get() as iStepDeliveryAddress;
    },

    addAdditionalAddress: (
        address = {
            amount: 0,
            salutation: "Herr",
            firstname: "",
            lastname: "",
            phone: "",
            street: "",
            zipcode: storage.get("hel:zipcode"),
            city: "",
            house_number: "",
        }
    ) => {
        set({
            additionalAddresses: [
                ...(get().additionalAddresses as []),
                address,
            ],
        });
    },

    updateAdditionalAddress: (address, index) => {
        set({
            additionalAddresses: get().additionalAddresses.map(
                (item: iAdditionalAddress, itemIndex: number) =>
                    itemIndex === index ? address : item
            ),
        });
    },

    setAdditionalAddresses: (additionalAddresses) => {
        set({ additionalAddresses: additionalAddresses });
    },

    removeAdditionalAddress: (index) => {
        set({
            additionalAddresses: get().additionalAddresses.filter(
                (item: iAdditionalAddress, itemIndex: number) =>
                    itemIndex !== index
            ),
        });
    },

    setAmountFirstUnloadingPoint: (amount) => {
        set({ amountFirstUnloadingPoint: amount });
    },

    getAmountForFirstUnloadingPoint: (amount) => {
        let firstAmount = amount;
        const additionalAddresses = get().additionalAddresses;
        if (additionalAddresses.length > 0) {
            additionalAddresses.map((additionalAddress: iAdditionalAddress) => {
                if (!isNaN(additionalAddress.amount)) {
                    firstAmount = firstAmount - additionalAddress.amount;
                }
            });
        }
        return firstAmount;
    },

    storeDeliveryAddressToStorage: () => {
        storage.setJson("hel:delivery-address", get().deliveryAddress);
        storage.setJson("hel:additional-addresses", get().additionalAddresses);
    },

    storeInvoiceAddressToStorage: () => {
        storage.setJson("hel:invoice-address", get().invoiceAddress);
        storage.set("hel:use-invoice-address", get().useInvoiceAddress);
    },

    deliveryAddressStepIsValid: () => {
        set({ errors: [] });
        let errors = [];
        const deliveryAddress = get().deliveryAddress;

        const validator = new Validator();
        const phoneIsValid = validator.getValidator(
            "phone",
            deliveryAddress.phone,
            true
        );

        const emailIsValid = validator.getValidator(
            "email",
            deliveryAddress.email,
            true
        );

        // Check if the fields are filled for delivery address
        if (deliveryAddress.firstname === "") {
            errors.push("deliveryAddress.firstname");
        }
        if (deliveryAddress.lastname === "") {
            errors.push("deliveryAddress.lastname");
        }
        if (!phoneIsValid()) {
            errors.push("deliveryAddress.phone");
        }
        if (!emailIsValid()) {
            errors.push("deliveryAddress.email");
        }
        if (deliveryAddress.street === "") {
            errors.push("deliveryAddress.street");
        }
        if (
            !deliveryAddress.house_number ||
            deliveryAddress.house_number === ""
        ) {
            errors.push("deliveryAddress.house_number");
        }
        if (deliveryAddress.city === "") {
            errors.push("deliveryAddress.city");
        }

        // Check if the fields are filled for invoice address
        const invoiceAddress = get().invoiceAddress;
        if (get().useInvoiceAddress) {
            if (invoiceAddress.firstname === "") {
                errors.push("invoiceAddress.firstname");
            }
            if (invoiceAddress.lastname === "") {
                errors.push("invoiceAddress.lastname");
            }
            if (invoiceAddress.street === "") {
                errors.push("invoiceAddress.street");
            }
            if (invoiceAddress.zipcode === "") {
                errors.push("invoiceAddress.zipcode");
            }
            if (invoiceAddress.city === "") {
                errors.push("invoiceAddress.city");
            }
            if (
                !invoiceAddress.house_number ||
                invoiceAddress.house_number === ""
            ) {
                errors.push("invoiceAddress.house_number");
            }
        } else {
            errors = errors.filter(
                (error) => !error.startsWith("invoiceAddress")
            );
        }

        // Check if the fields are filled for additional unloading points
        const additionalAddresses = get().additionalAddresses;
        const up = get().up;
        if (up > 1) {
            for (let i = 0; i < up - 1; i++) {
                if (additionalAddresses[i].firstname === "") {
                    errors.push(`additionalAddresses.${i}.firstname`);
                }
                if (additionalAddresses[i].lastname === "") {
                    errors.push(`additionalAddresses.${i}.lastname`);
                }
                if (additionalAddresses[i].street === "") {
                    errors.push(`additionalAddresses.${i}.street`);
                }
                if (additionalAddresses[i].city === "") {
                    errors.push(`additionalAddresses.${i}.city`);
                }
                if (additionalAddresses[i].phone === "") {
                    errors.push(`additionalAddresses.${i}.phone`);
                }
                if (
                    isNaN(Number(additionalAddresses[i].amount)) ||
                    additionalAddresses[i].amount <= 0
                ) {
                    errors.push(`additionalAddresses.${i}.amount`);
                }
                if (
                    !additionalAddresses[i].house_number ||
                    additionalAddresses[i].house_number === ""
                ) {
                    errors.push(`additionalAddresses.${i}.house_number`);
                }
            }
        }
        get().setErrors(errors);
        if (errors.length <= 0) {
            return true;
        } else {
            if (errors.length > 0) {
                const elements = document.getElementsByClassName("help");
                if (elements) {
                    setTimeout(() => {
                        elements[0].scrollIntoView();
                    }, 100);
                }
            }
            return false;
        }
    },

    storeDeliveryAddressToServer: async (
        checkoutData,
        notify,
        isOverview = false,
        onSuccess
    ): Promise<iCheckoutData> => {
        let checkoutDataResponse = { ...checkoutData };
        set({ errors: [] });
        const data = {
            hash: checkoutData.user_hash,
            amount: checkoutData.amount,
            deliveryAddress: get().deliveryAddress,
            additionalAddresses: get().additionalAddresses,
            useInvoiceAddress: get().useInvoiceAddress,
            invoiceAddress: get().invoiceAddress,
        };

        await errorAwareApiFetch<{ data: any }>(
            `/heatingoil/checkout/deliveryAddress`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        )
            .then(({ data }) => {
                checkoutDataResponse = data;
                if (onSuccess) {
                    onSuccess();
                }
            })
            .catch(({ data }) => {
                const errors = Object.keys(data.errors);
                set({ errors: errors });
                notify("Fehlgeschlagen", data.message);
                if (!isOverview) {
                    navigate(
                        resolveWebView("heizoel/bestellung/lieferadresse/")
                    );
                }
            });
        return checkoutDataResponse;
    },

    setDeliveryAddressStorageToState: () => {
        set({ deliveryAddress: storage.getJson("hel:delivery-address") });
        const addresses = storage.getJson("hel:additional-addresses");
        set({ additionalAddresses: addresses });
        set({ up: Number(addresses.length) + 1 });
        storage.set("hel:up", Number(addresses.length) + 1);
    },

    setInvoiceAddressStorageToState: () => {
        set({ invoiceAddress: storage.getJson("hel:invoice-address") });
        set({ useInvoiceAddress: storage.getJson("hel:use-invoice-address") });
    },

    clearDeliveryAddress: () => {
        storage.remove("hel:selected-city");
        storage.remove("hel:selected-street");
        set({
            deliveryAddress: {
                ...get().deliveryAddress,
                zipcode: storage.get("hel:zipcode"),
                street: "",
                house_number: "",
                city: "",
            },
        });

        const up = storage.getInt("hel:up", 1);
        const additionalAddresses = get().additionalAddresses;
        const addresses = [] as Array<iAdditionalAddress>;
        set({ up: up });
        if (additionalAddresses.length < up - 1) {
            additionalAddresses.forEach((address) => {
                addresses.push({
                    ...address,
                    zipcode: storage.get("hel:zipcode"),
                });
            });
        } else {
            for (let i = additionalAddresses.length; i < up - 1; i++) {
                addresses.push({
                    amount: 0,
                    salutation: "Herr",
                    firstname: "",
                    lastname: "",
                    phone: "",
                    street: "",
                    zipcode: storage.get("hel:zipcode"),
                    city: "",
                    house_number: "",
                });
            }
        }

        set({ additionalAddresses: addresses });
    },
}));

export default useStore;
